import { request } from '@/api/request'
import type { AuthSession } from '@/lib/authToken'

/** Tokens from `POST /auth/signin` (Cognito via the backend proxy, as in CGT). */
export interface AuthTokens {
  accessToken: string
  idToken: string
  refreshToken?: string
  expiresIn?: number
  tokenType?: string
}

/** Returned by `POST /auth/signin` when Cognito requires a new password first. */
export interface NewPasswordChallenge {
  challenge: 'NEW_PASSWORD_REQUIRED'
  session: string
  email: string
}

export function isChallenge(res: AuthTokens | NewPasswordChallenge): res is NewPasswordChallenge {
  return 'challenge' in res
}

export const authApi = {
  /** Dev-only email login (API must have dev login enabled). */
  devLogin: (email: string): Promise<{ accessToken: string }> =>
    request<{ accessToken: string }>('/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuthRedirect: true,
    }),

  /**
   * Cognito email/password sign-in. A 401 means a wrong credential, not a dead session.
   * A first sign-in may answer with a `NEW_PASSWORD_REQUIRED` challenge instead of tokens.
   */
  signin: (email: string, password: string): Promise<AuthTokens | NewPasswordChallenge> =>
    request<AuthTokens | NewPasswordChallenge>('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      skipAuthRedirect: true,
    }),

  /** The current principal with resolved permissions and scope. */
  me: (): Promise<AuthSession> => request<AuthSession>('/auth/me'),
}
